import React, { useState, useRef, useEffect } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import {
    XMarkIcon,
    EllipsisVerticalIcon,
    TrashIcon,
    PaintBrushIcon,
    CheckIcon
} from '@heroicons/react/24/outline';
import { observer } from 'mobx-react-lite';
import SelectDropdown from '../Components/Dropdown/SelectDropdown';
import { ViewTypes } from '../../models/SheetModel';

export const SheetBarSelector = observer(({ sheet, sheetVM, index, selected, onSelect, onRemove, onRename }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(sheet.name || `Sheet ${index + 1}`);
    const inputRef = useRef(null);
    const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);
    
    const handleSelect = React.useCallback(() => {
        onSelect(index);
    }, [onSelect, index]);

    const handleRemove = React.useCallback((e) => {
        e.stopPropagation();
        onRemove(index);
    }, [onRemove, index]);

    const handleDoubleClick = (e) => {
        e.stopPropagation();
        setIsEditing(true);
        setEditValue(sheet.name || `Sheet ${index + 1}`);
    };

    const handleBlur = () => {
        setIsEditing(false);
        const newName = editValue.trim();
        const oldName = sheet.name;
        console.log('[SheetBarSelector] handleBlur:', { newName, oldName, index });
        if (newName && newName !== oldName) {
            console.log('[SheetBarSelector] Calling onRename with:', { index, newName });
            onRename(index, newName);
        } else {
            console.log('[SheetBarSelector] onRename not called. Condition not met:', { newName, oldName });
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            inputRef.current?.blur();
        } else if (e.key === 'Escape') {
            setIsEditing(false);
            setEditValue(sheet.name || `Sheet ${index + 1}`);
        }
    };

    useEffect(() => {
        if (isEditing) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [isEditing]);
    
    const handleActionSelect = (actionValue) => {
        setActionsDropdownOpen(false);

        if (actionValue === 'delete') {
            if (sheetVM.visibleSheets.length > 1) {
                onRemove(index);
            }
        } else if (actionValue === 'color') {
            const color = prompt("Enter color (e.g., #ff0000 or red):", sheet.color || "#ffffff");
            if (color) {
                sheetVM.updateSheetColor(index, color);
            }
        } else if (ViewTypes[actionValue]) {
            sheetVM.updateSheetView(index, actionValue);
        } else {
            console.warn("Unknown sheet action:", actionValue);
        }
    };

    const getActionOptions = () => {
        const options = [];

        options.push({ label: "Set View Type", type: "header" });
        Object.values(ViewTypes).forEach(view => {
            const isCurrent = sheet.name.startsWith(view.icon + ' ');
            options.push({
                label: view.label,
                value: view.id,
                icon: <span>{view.icon}</span>,
                selected: isCurrent
            });
        });

        options.push({ type: 'divider' });

        options.push({
            label: "Set Tab Color",
            value: 'color',
            icon: <PaintBrushIcon className="h-4 w-4" />
        });

        options.push({ type: 'divider' });

        options.push({
            label: "Delete Sheet",
            value: 'delete',
            icon: <TrashIcon className="h-4 w-4" />,
            destructive: true,
            disabled: sheetVM.visibleSheets.length <= 1
        });

        return options;
    };

    return (
        <Draggable 
            draggableId={sheet.id?.toString() || index.toString()} 
            index={index}
        >
            {(provided, snapshot) => {
                const customStyle = sheet.color ? { backgroundColor: sheet.color } : {};

                return (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{ ...provided.draggableProps.style, ...customStyle }}
                        onClick={handleSelect}
                        className={`
                            inline-flex items-center rounded-lg px-3 py-1.5 pr-2 
                            text-sm font-semibold flex-shrink-0 mr-1.5 
                            transition-colors cursor-pointer group
                            ${selected ? 'border-2 border-zinc-950' : 'border-2 border-transparent opacity-75 hover:opacity-100'} 
                            ${selected 
                                ? sheet.color ? 'ring-0' : 'bg-lime-green-200 text-zinc-950' 
                                : sheet.color ? 'hover:brightness-95' : 'text-zinc-600 hover:text-lime-green-900 hover:bg-lime-green-200'}
                            ${snapshot.isDragging ? 'ring-2 ring-zinc-500 ring-opacity-50' : ''}
                            ${sheet.color ? `text-black ${selected ? '' : 'bg-opacity-50 hover:bg-opacity-75'}` : ''}
                        `}
                    >
                        <div className="flex items-center flex-grow min-w-0">
                            {isEditing ? (
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={handleBlur}
                                    onKeyDown={handleKeyDown}
                                    className={`
                                        bg-transparent border-none outline-none p-0 flex-grow
                                        ${selected ? 'text-zinc-950' : 'text-zinc-900'}
                                        focus:ring-0 min-w-[60px] w-full
                                        ${sheet.color ? 'placeholder-gray-700' : ''}
                                    `}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            ) : (
                                <span onDoubleClick={handleDoubleClick} className="truncate">
                                    {sheet.name || `Sheet ${index + 1}`}
                                </span>
                            )}
                        </div>
                        <div className="flex-shrink-0 ml-1.5 flex items-center justify-center">
                            <SelectDropdown
                                options={getActionOptions()}
                                onChange={handleActionSelect}
                                showDropdownButton={false}
                                onOpenChange={setActionsDropdownOpen}
                                isOpen={actionsDropdownOpen}
                                customTrigger={
                                    <button
                                        className={`
                                            p-0.5 rounded-full flex items-center justify-center
                                            hover:bg-zinc-200 transition-opacity
                                            ${selected ? 'text-zinc-950 text-opacity-60 hover:bg-zinc-950 hover:bg-opacity-10' : 'text-zinc-950 text-opacity-60 hover:bg-zinc-950 hover:bg-opacity-10'}
                                            ${actionsDropdownOpen ? 'opacity-100' : ''} 
                                        `}
                                        onClick={(e) => {
                                            console.log('Dropdown button clicked, current state:', actionsDropdownOpen);
                                            setActionsDropdownOpen(!actionsDropdownOpen);
                                        }}
                                    >
                                        <EllipsisVerticalIcon className="h-4 w-4" />
                                    </button>
                                }
                                customStyles={{
                                    dropdown: "w-48",
                                    option: "text-xs",
                                    header: "text-xs font-semibold text-gray-500 px-2 pt-1 pb-0.5"
                                }}
                            />
                        </div>
                    </div>
                );
            }}
        </Draggable>
    );
});